/*
  drawlz-policy.js — makes the four policy pages navigable WITHOUT editing
  a single word of their content.

  WHY IT WORKS THIS WAY
  Page bodies live in Shopify Pages, not in the theme, so they are shared by
  every theme — editing one changes the LIVE store. They are also legal text,
  where a hand-retyped clause is a real liability, not a cosmetic bug. So
  nothing here rewrites text. It re-wraps the structure the bodies already
  have:

    · Return uses <h3> for its sections, Shipping uses <h4>.
    · Terms marks headings as <p><strong>INTRODUCTION</strong></p>.
    · Privacy is one dense <p> whose sections are ALL-CAPS lines separated
      by <br>.

  Each of those is a heading that simply is not tagged as one. This finds
  them, tags them, gives them ids, builds the jump links, and inserts the
  back-to-top links. Every character of the original text survives — the
  ALL-CAPS branch reuses the existing markup verbatim and only regroups it
  around the line breaks.

  Heading LEVEL is normalised per page rather than assumed: whatever the
  shallowest heading level on the page is, that becomes the section level.
  That is why one file handles h3-based, h4-based and untagged bodies.
*/
(function () {

  /* Length caps are the guard against false positives. Real legal bodies
     contain long ALL-CAPS disclaimer paragraphs (Terms has several) and long
     bold lead-ins — those are prose, not headings, and the cap excludes them.
     A trailing : ; , . also means a lead-in rather than a heading. */
  var MAX_HEAD = 70;

  function looksLikeHeading(t) {
    if (!t) return false;
    t = t.trim();
    if (t.length < 5 || t.length > MAX_HEAD) return false;
    if (/[.:;,]$/.test(t)) return false;
    return true;
  }
  function isAllCaps(t) {
    return looksLikeHeading(t) && /[A-Z]/.test(t) && !/[a-z]/.test(t);
  }

  function slug(t, used) {
    var s = (t || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);
    if (!s) s = 'section';
    var base = s, n = 2;
    while (used[s]) { s = base + '-' + n; n++; }
    used[s] = true;
    return s;
  }

  /* ---- Terms: <p><strong>HEADING</strong></p> -> a real heading ---- */
  function promoteStrongParagraphs(body) {
    var ps = Array.prototype.slice.call(body.querySelectorAll('p'));
    ps.forEach(function (p) {
      var kids = Array.prototype.filter.call(p.childNodes, function (n) {
        return !(n.nodeType === 3 && !n.nodeValue.trim());
      });
      if (kids.length !== 1) return;
      var only = kids[0];
      if (!only.tagName || only.tagName !== 'STRONG') return;
      var txt = only.textContent.trim();
      if (!looksLikeHeading(txt)) return;
      var h = document.createElement('h2');
      h.textContent = txt;                       /* text preserved exactly */
      p.parentNode.replaceChild(h, p);
    });
  }

  /* ---- Privacy: one dense <p>, ALL-CAPS lines between <br>s ----
     Splits on the line breaks and regroups. The non-heading lines are put
     back with their original markup untouched. */
  function explodeDenseParagraphs(body) {
    var ps = Array.prototype.slice.call(body.querySelectorAll('p'));
    ps.forEach(function (p) {
      var html = p.innerHTML;
      if (!/<br\s*\/?>/i.test(html)) return;
      var lines = html.split(/<br\s*\/?>/i);

      var hasHeading = lines.some(function (ln) {
        return isAllCaps(ln.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim());
      });
      if (!hasHeading) return;                   /* ordinary <br> use — leave alone */

      var out = [], buf = [];
      function flush() {
        var joined = buf.join('<br>');
        if (joined.replace(/<[^>]*>/g, '').trim()) {
          var el = document.createElement('p');
          el.innerHTML = joined;
          out.push(el);
        }
        buf = [];
      }
      lines.forEach(function (ln) {
        var txt = ln.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
        if (isAllCaps(txt)) {
          flush();
          var h = document.createElement('h2');
          h.textContent = txt;
          out.push(h);
        } else {
          buf.push(ln);
        }
      });
      flush();

      if (!out.length) return;
      var frag = document.createDocumentFragment();
      out.forEach(function (el) { frag.appendChild(el); });
      p.parentNode.replaceChild(frag, p);
    });
  }

  /* ---- whatever level the page uses, treat the shallowest as "section" ---- */
  function classify(body) {
    var all = Array.prototype.slice.call(body.querySelectorAll('h2,h3,h4,h5'));
    if (!all.length) return [];
    var levels = all.map(function (h) { return parseInt(h.tagName.slice(1), 10); });
    var top = Math.min.apply(null, levels);
    var heads = [];
    all.forEach(function (h) {
      var lvl = parseInt(h.tagName.slice(1), 10);
      if (lvl === top) { h.classList.add('policy-h2'); heads.push(h); }
      else { h.classList.add('policy-h3'); }
    });
    return heads;
  }

  function wrapTables(body) {
    Array.prototype.slice.call(body.querySelectorAll('table')).forEach(function (t) {
      if (t.parentNode && t.parentNode.classList.contains('policy-tablewrap')) return;
      var w = document.createElement('div');
      w.className = 'policy-tablewrap';
      t.parentNode.insertBefore(w, t);
      w.appendChild(t);
    });
  }

  function init(root) {
    if (!root || root.dataset.policyBound === '1') return;
    root.dataset.policyBound = '1';

    var body = root.querySelector('[data-policy-body]');
    if (!body) return;

    promoteStrongParagraphs(body);
    explodeDenseParagraphs(body);
    wrapTables(body);

    var heads = classify(body);
    if (!heads.length) return;

    heads[0].classList.add('is-first');

    var used = {};
    heads.forEach(function (h) {
      if (!h.id) h.id = slug(h.textContent, used);
      else used[h.id] = true;
    });

    /* ---- jump links ---- */
    var mode = root.getAttribute('data-toc-mode') || 'auto';
    var min = parseInt(root.getAttribute('data-toc-min'), 10) || 5;
    var wantToc = mode === 'always' || (mode === 'auto' && heads.length >= min);

    var toc = root.querySelector('[data-policy-toc]');
    if (toc && wantToc) {
      var title = root.getAttribute('data-toc-title') || 'On this page';
      var t = document.createElement('p');
      t.className = 'policy-toc__t';
      t.textContent = title;
      var ol = document.createElement('ol');
      heads.forEach(function (h) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.href = '#' + h.id;
        a.textContent = h.textContent;
        li.appendChild(a);
        ol.appendChild(li);
      });
      toc.appendChild(t);
      toc.appendChild(ol);
      toc.hidden = false;
      toc.setAttribute('aria-label', title);
      /* the anchor the back-to-top links return to */
      root.id = root.id || 'policy-top';
    }

    /* ---- back to top, at the end of each section ----
       Inserted BEFORE each heading after the first, which is the end of the
       section above it. Skipped entirely when there are no jump links, since
       there would be nothing at the top worth returning to. */
    if (root.getAttribute('data-backtotop') === '1' && wantToc) {
      var label = root.getAttribute('data-backtotop-label') || 'Back to top';
      var target = '#' + root.id;
      heads.forEach(function (h, i) {
        if (i === 0) return;
        var a = document.createElement('a');
        a.className = 'policy-top';
        a.href = target;
        a.textContent = '↑ ' + label;
        h.parentNode.insertBefore(a, h);
      });
    }
  }

  function scan() {
    Array.prototype.slice.call(document.querySelectorAll('[data-policy]')).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan);
  else scan();
})();
